import axios from 'axios';
import CRMManager from './crm';

const getFormattedTime = (timestamp, unit = 'sec') => {
	// https://stackoverflow.com/questions/1322732/convert-seconds-to-hh-mm-ss-with-javascript
	if (!timestamp) return;
	let timestampInMillisecond;
	if (unit === 'sec') {
		timestampInMillisecond = timestamp * 1000;
	}
	if (unit === 'ms') {
		timestampInMillisecond = timestamp;
	}
	return new Date(timestampInMillisecond).toISOString().substr(11, 8);
};

function getCurrentTime() {
	return new Date().toLocaleTimeString([], { timeStyle: 'short' }).replace('PM', 'pm').replace('AM', 'am');
}

function uuidv4() {
	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
		(c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
	);
}

function comparer(otherArray) {
	return function (current) {
		return (
			otherArray.filter(function (other) {
				return other.deviceId == current.deviceId;
			}).length == 0
		);
	};
}

function uniqueList(arr, key) {
	// remove duplicate objects from an array of objects by a specific key
	// for ex: if key=_id, filter array to all objects w unique _id values
	return [...new Map(arr.map((item) => [item[key], item])).values()];
}

// We cache the data in this variable so we can remove the element from the DOM.
const dataCache = {};

const getDataFromEl = (id) => {
	if (!Object.keys(dataCache).includes(id)) {
		const el = document.getElementById(id);
		const string = el.getAttribute('data-json');
		el.remove();
		dataCache[id] = JSON.parse(string);
	}

	return dataCache[id];
};

// this method is used by the unified app components
// it does not retrieve from cache but only contributes to it as data may change
const getDataFromDomEl = (id, domEl) => {
	if (!domEl) {
		console.warn('no dom el for id', id);
		return;
	}
	const el = domEl.querySelector('#' + id);
	if (!el) {
		console.warn('no element for id', id);
		return;
	}
	const string = el.getAttribute('data-json');
	el.remove();
	dataCache[id] = JSON.parse(string);
	return dataCache[id];
};

const getGclid = () => {
	let rt = JSON.parse(localStorage.getItem('gclid'));
	if (rt) {
		rt = rt.value;
	}
	return rt;
};

function convertRTMPToArray(rtmp) {
	const rtmpArray = Object.keys(rtmp).map((platformName) => rtmp[platformName]);
	return { rtmp: rtmpArray };
}

function convertRTMPToObject(rtmp) {
	const platformNames = ['youtube', 'twitter', 'facebook', 'twitch', 'linkedin', 'custom'];
	const rtmpObject = {};

	platformNames.forEach((platformName) => {
		rtmpObject[platformName] = rtmp?.find(
			(platform) => platform.id === (platformName === 'twitter' ? 'pscp' : platformName)
		) || {
			id: platformName === 'twitter' ? 'pscp' : platformName,
			isActive: false,
			streamURL: null,
			streamName: null,
		};
	});
	return rtmpObject;
}

function capitalizeText(text) {
	if (!text) {
		return '';
	} else {
		return text
			.toLowerCase()
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}
}

function capitalizeFirstLetter(string) {
	return string?.charAt(0).toUpperCase() + string.slice(1);
}

function isEllipsisActive(el) {
	return !!el && el.offsetWidth < el.scrollWidth;
}

// from the interwebs convert query paramers to object
function getQueryParams() {
	return window.location.search
		.replace('?', '')
		.split('&')
		.reduce((r, e) => ((r[e.split('=')[0]] = decodeURIComponent(e.split('=')[1])), r), {});
}

function detectSpeaker(mediaHandlerPeer, handleChange, volumeThreshold) {
	const periodBeforeActive = 200;
	const periodBeforeInactive = 800;
	let activity = null;

	const audioLevelUpdated = (event) => {
		const now = Date.now();
		if (event.audioLevel > volumeThreshold) {
			if (!activity) {
				activity = { timestamp: now, talking: false };
			} else if (activity.talking) {
				activity.timestamp = now;
			} else if (now - activity.timestamp > periodBeforeActive) {
				activity.talking = true;
				handleChange(true);
			}
		} else if (activity && now - activity.timestamp > periodBeforeInactive) {
			if (activity.talking) {
				handleChange(false);
			}
			activity = null;
		}
	};

	mediaHandlerPeer.on('audioLevelUpdated', audioLevelUpdated);
	return () => {
		mediaHandlerPeer.removeListener('audioLevelUpdated', audioLevelUpdated);
	};
}

function getUserActivity() {
	const userActivity = localStorage.getItem('userActivity');
	return JSON.parse(userActivity);
}

function updateUserActivity(activity) {
	const userActivity = JSON.parse(localStorage.getItem('userActivity'));
	const updatedUserActivity = { ...userActivity, ...activity };
	localStorage.setItem('userActivity', JSON.stringify(updatedUserActivity));
}

function getRecordingErrorMessage(severity, isScreenShareTimeout) {
	if (isScreenShareTimeout) {
		return "We couldn't start the HD recording for this screen.\n";
	}
	switch (severity) {
		case 10:
			return "This recording failed and is stopped. Please check the host's sidebar for more info.\n";
		case 20:
			return 'A backup recording started for this user.\n';
		case 30:
			return 'Low browser storage for this participant, make sure they are not in incognito mode.\n';
		default:
			break;
	}
}

function getMobileOperatingSystem() {
	const userAgent = navigator.userAgent || navigator.vendor || window.opera;

	// Windows Phone must come first because its UA also contains "Android"
	if (/windows phone/i.test(userAgent)) {
		return 'Windows Phone';
	}

	if (/android/i.test(userAgent)) {
		return 'Android';
	}

	// iOS detection from: http://stackoverflow.com/a/9039885/177710
	if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
		return 'iOS';
	}

	return 'unknown';
}

function isTakeArchived(take) {
	// New parameter isAvailable taking precedence over old logic is it's set in the db
	if (take?.archiveInfo?.isAvailable !== undefined) {
		return !take.archiveInfo.isAvailable;
	} else {
		let oldTakeIsArchived;
		// for the takes that dont have the available until property
		if (!take?.archiveInfo?.availableUntil) {
			const ninetyDaysFromRecording = new Date(take?.timestamp).getTime() + 90 * 24 * 60 * 60 * 1000;
			if (
				new Date(ninetyDaysFromRecording) < new Date() &&
				new Date(ninetyDaysFromRecording) < new Date('2021-11-04')
			) {
				oldTakeIsArchived = true;
			}
		}
		const isTakeArchived =
			(take?.archiveInfo?.availableUntil && new Date() > new Date(take?.archiveInfo?.availableUntil)) ||
			oldTakeIsArchived;
		return isTakeArchived;
	}
}

function chunkString(str, size) {
	const numChunks = Math.ceil(str.length / size);
	const chunks = new Array(numChunks);

	for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
		chunks[i] = str.substr(o, size);
	}
	return chunks;
}

function downloadURL(url, filename) {
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();
}

const CLIENT_ID_LOCAL_STORAGE_KEY = 'rs.clientID';

const getClientID = () => {
	const clientIDFromLocalStorage = localStorage.getItem(CLIENT_ID_LOCAL_STORAGE_KEY);
	if (clientIDFromLocalStorage) {
		return clientIDFromLocalStorage;
	}

	const clientID = uuidv4();
	localStorage.setItem(CLIENT_ID_LOCAL_STORAGE_KEY, clientID);
	return clientID;
};

const setPage = (title, bodyClassNames = [], { showCRMLauncher = true } = {}) => {
	document.title = title;
	const body = document.body;
	body.classList.remove(...body.classList);
	bodyClassNames.forEach((className) => body.classList.add(className));
	if (showCRMLauncher) {
		CRMManager.showLauncher();
	} else {
		CRMManager.hideLauncher();
	}
};

const parseRTMPArray = (rtpms) => {
	const rt = rtpms
		.filter((rtmp) => rtmp.isActive)
		.map((rtmp) => ({ url: rtmp.streamURL, streamKey: rtmp.streamName, provider: rtmp.id }));

	return rt && rt.length ? rt : null;
};

const delay = (ms) => {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
};

const fetchStudioStateData = ({ slug }) => {
	console.log('getting broadcast status ');
	return new Promise((resolve, reject) => {
		axios
			.get(`/api/${slug}/status`)
			.then((res) => {
				console.log(res, 'res');
				const response = res.data;
				response.status = response.status || 'waiting';
				response.status = response.status == 'started' ? 'started' : 'idle';
				resolve(response);
			})
			.catch((err) => console.log('error', err));
	});
};

const special = [
	'zeroth',
	'first',
	'second',
	'third',
	'fourth',
	'fifth',
	'sixth',
	'seventh',
	'eighth',
	'ninth',
	'tenth',
	'eleventh',
	'twelfth',
	'thirteenth',
	'fourteenth',
	'fifteenth',
	'sixteenth',
	'seventeenth',
	'eighteenth',
	'nineteenth',
];
const deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

function getOrdinalNumber(n) {
	try {
		if (n < 20) return special[n];
		if (n % 10 === 0) return deca[Math.floor(n / 10) - 2] + 'ieth';
		return deca[Math.floor(n / 10) - 2] + 'y-' + special[n % 10];
	} catch (e) {
		return n.toString();
	}
}

function getCookie(name) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop().split(';').shift();
}

export {
	getFormattedTime,
	getCurrentTime,
	uuidv4,
	comparer,
	uniqueList,
	getDataFromEl,
	convertRTMPToObject,
	convertRTMPToArray,
	capitalizeText,
	isEllipsisActive,
	getQueryParams,
	getGclid,
	detectSpeaker,
	getUserActivity,
	updateUserActivity,
	getRecordingErrorMessage,
	getMobileOperatingSystem,
	isTakeArchived,
	downloadURL,
	chunkString,
	capitalizeFirstLetter,
	getDataFromDomEl,
	getClientID,
	setPage,
	parseRTMPArray,
	delay,
	fetchStudioStateData,
	getOrdinalNumber,
	getCookie,
};
